import { EVENT } from "@/constants/event";
import { safeParse } from "@/utils/safeParse";
import classNames from "classnames";
import Link from "next/link";
import { useRouter } from "next/router";
import { useEffect, useMemo, useRef, useState } from "react";
import GlobalBannerContainer from "../GlobalBanner/Container";
import HBLogo from "../HBLogo";
import { useLogin } from "../LoginModal";
import styles from "./index.module.less";
import RightNav from "./rightNav";

import { AD_POSITION } from "@/constants/ads";
import { LOCAL_STORAGE_KEY } from "@/constants/localStorageKey";
import useAppStore from "@/context/store";
import { goGdMerge } from "@/pageComponents/GdMerge";
import { getAds } from "@/services";
import { isGdMerged } from "@/utils/gaoding";
import useABTest from "@/utils/hooks/useABtest";
import { useMemoizedFn, useUpdateEffect } from "ahooks";
import { last, omit } from "lodash";
import dynamic from "next/dynamic";
import { match } from "path-to-regexp";
import { When } from "react-if";
import HBIcon from "../HBIcon";
import { TrackerA, TrackerDiv } from "../Tracker";
import Avatar from "./Avatar";
import CreateEntry from "./CreateEntry";
import { HeaderContext, useIsNewHeader } from "./headerContext";
import { VipRightNav } from "./rightNav/vip";

const Search = dynamic(import("./search"), { ssr: false });

export default function Header({ isMobile, searchWord }) {
  const router = useRouter();

  const newHeaderAb = useABTest("hb_new_header_ab", "new");
  const isNewHeader = newHeaderAb.value === "new";

  const {
    user,
    unreadNum,
    dmUnread,
    headerHeight,
    setStore,
    ads,
    adTopConfig,
  } = useAppStore(
    ({
      user,
      unreadNum,
      dmUnread,
      headerHeight,
      setStore,
      ads,
      adTopConfig,
    }) => ({
      user,
      unreadNum,
      dmUnread,
      headerHeight,
      setStore,
      ads,
      adTopConfig,
    })
  );

  // 首页控制搜索框的显示隐藏
  const [searchVisible, setSearchVisible] = useState(false);

  const headerRef = useRef(null);
  const isHome = router.pathname === "/";

  // 切换页面时，获取指定页面吸顶广告
  useUpdateEffect(() => {
    const matchConfig = adTopConfig?.ads?.find((ad) => {
      return match(ad?.path)(router.asPath);
    });
    // 移除指定页面广告
    const filterFn = (ads) => {
      return (ads || []).filter(
        (ad) => ad?.position?.id !== AD_POSITION.TOP_PAGE
      );
    };

    // 当有指定页面吸顶广告配置时
    if (matchConfig) {
      getAds({
        positions: [AD_POSITION.TOP_PAGE],
        variables: {
          user_tag: [matchConfig.user_tag],
        },
      }).then((res) => {
        // 更新广告信息, 删除旧的指定页面广告
        useAppStore.setState((prev) => {
          return {
            ads: [...res, ...filterFn(prev.ads)],
          };
        });
      });
    } else {
      // 移除指定页面广告
      useAppStore.setState((prev) => {
        return {
          ads: [...filterFn(prev.ads)],
        };
      });
    }
  }, [router.asPath]);

  useEffect(() => {
    const onHanlder = (event) => {
      setSearchVisible(event.detail);
    };
    window.addEventListener(EVENT.CHANGE_SEARCH_INPUT_VISIBLE, onHanlder);
    return () => {
      window.removeEventListener(EVENT.CHANGE_SEARCH_INPUT_VISIBLE, onHanlder);
    };
  }, []);

  return (
    <HeaderContext.Provider value={{ isNewHeader }}>
      <div
        className={classNames([
          styles.header,
          isHome ? styles.home : null,
          isNewHeader && styles.newHeader,
        ])}
        data-content-source="导航栏"
        ref={headerRef}
      >
        <When condition={!isHome}>
          <GlobalBannerContainer />
        </When>
        <div className={styles.wrap}>
          <div className={classNames(styles.container)}>
            <Link href={user ? "/follow" : "/"} passHref>
              <TrackerA
                onClick={(event) => {
                  if (user) {
                    onClickAction(event, router);
                  }
                }}
                data-dispatch-event={EVENT.FOLLOW_PIN_LIST_RESET}
                data-gd-click="button_click"
                data-button-name="LOGO"
                rel="nofollow"
              >
                <HBLogo isHome={isHome} />
              </TrackerA>
            </Link>
            <LeftNav user={user} isHome={isHome} />
            <div className={styles.searchWrapper}>
              {!isMobile && (
                <Search
                  className={classNames({
                    [styles.hide]: isHome && !searchVisible,
                  })}
                  initValue={searchWord}
                  bordered={!isNewHeader}
                />
              )}
            </div>
            <VipRightNav />
            {!isHome && <CreateEntry />}
            <RightNav user={user} unreadNum={unreadNum} dmUnread={dmUnread} />
            {user?.user_id ? <Avatar /> : <Login />}
          </div>
        </div>
      </div>
      <When condition={!isHome}>
        {/* 
        - 如果是通过滚动使得广告消失，则高度不变，依然为140
        - 如果是通过按钮点击关闭广告则，高度为70
      */}
        <div
          className={styles.placeholder}
          style={{
            // height: `${spaceHeight || headerHeight}px`,
            height: `${headerHeight}px`,
            transition: ".2s ",
          }}
        />
      </When>
    </HeaderContext.Provider>
  );
}

function onClickAction(e, router) {
  if (e.ctrlKey || e.metaKey) {
    return;
  }
  e.preventDefault();
  const targetHref = e.currentTarget.getAttribute("href");
  if (router.pathname === targetHref) {
    const dispatchEvent = e.currentTarget.getAttribute("data-dispatch-event");
    window.dispatchEvent(new Event(dispatchEvent));
  } else {
    router.push(targetHref);
  }
}

function LeftNav({ user, isHome }) {
  const config = useAppStore((s) => s.config);
  const isNewHeader = useIsNewHeader();
  /**
   * 导航配置 https://grocery.huaban.com/put/25
   * @type {{
   * key: string,
   * label: string,
   * href: string,
   * ref?: string
   * target?: string,
   * tag?: string,
   * style?: React.CSSProperties,
   * }[]}
   */
  const navLinks = useMemo(() => {
    const list = isNewHeader ? config?.newHeadNav : config?.headNav;
    const navs =
      list?.map((nav) => {
        if (nav.attribute) {
          nav[nav.attribute] = true;
        }
        return nav;
      }) || [];
    if (navs.length) {
      last(navs).style = {
        paddingRight: 10,
      };

      const lastGdNav = last(
        navs.filter((nav) => nav.attribute !== "data-gdmerge-abcontrol-reverse")
      );

      if (lastGdNav) {
        lastGdNav.style = {
          paddingRight: 10,
        };
      }
    }

    return navs;
  }, [config, isNewHeader]);

  const [navTagsShow, setNavTagsShow] = useState(false);

  // 隐藏不需要显示tag的navLinks
  const hideNavTag = useMemoizedFn((key) => {
    // 不需要显示tag的navLinks的key
    const keys = navLinks.filter((s) => !s.tag).map((s) => s.key);
    // 获取localStorage的值
    let navTagsShow;
    navTagsShow = safeParse(
      localStorage.getItem(LOCAL_STORAGE_KEY.HB_HEADER_NAV_TAG) || "{}",
      () => {
        return {};
      }
    );

    // omit是为了删除不需要显示tag的值
    if (key) {
      setNavTagsShow({
        ...omit(navTagsShow, keys),
        [key]: true,
      });
    } else {
      setNavTagsShow({
        ...omit(navTagsShow, keys),
      });
    }
  });

  useEffect(() => {
    hideNavTag(); //删除不需要显示tag的localStorage记录的值，避免下次配置不显示tag
  }, [hideNavTag]);

  useEffect(() => {
    // 同步localStorage
    localStorage.setItem(
      LOCAL_STORAGE_KEY.HB_HEADER_NAV_TAG,
      JSON.stringify(navTagsShow)
    );
  }, [navTagsShow]);

  const router = useRouter();
  const isDescovery =
    router.pathname.includes("/discovery") &&
    !router.pathname.includes("/discovery/[categoryId]/materials");

  const followActive = useMemo(() => {
    return /^\/follow(\/[[\]\w]+\/[[\]\w]+)?$/g.test(router.pathname);
  }, [router]);

  return (
    <div className={styles.nav}>
      {user && (
        <Link href="/follow" passHref>
          <TrackerA
            className={classNames({
              [styles.active]: followActive,
            })}
            onClick={(event) => {
              onClickAction(event, router);
            }}
            data-dispatch-event={EVENT.FOLLOW_PIN_LIST_RESET}
            data-gd-click="button_click"
            data-button-name="关注"
          >
            关注
          </TrackerA>
        </Link>
      )}
      <Link href="/discovery" passHref>
        <TrackerA
          className={isDescovery ? styles.active : ""}
          onClick={(event) => {
            onClickAction(event, router);
          }}
          data-dispatch-event={EVENT.DISCOVERY_PIN_LIST_RESET}
          data-gd-click="button_click"
          data-button-name="发现"
        >
          发现
        </TrackerA>
      </Link>
      {!isHome && <span className={styles.divider}></span>}

      {navLinks.map((item) => (
        <NavLink
          key={item.key}
          className={classNames({
            [styles.active]: router.pathname.includes(item.href),
          })}
          {...item}
          tagShow={item.tag && navTagsShow && !navTagsShow[item.key]}
          onClick={(evt) => {
            hideNavTag(item.key);
            if (
              item.attribute === "data-gdmerge-abcontrol" &&
              user &&
              !isGdMerged(user)
            ) {
              goGdMerge({
                redirectUri: item.href,
              });
              evt.preventDefault();
            }
          }}
        />
      ))}
    </div>
  );
}

function Login() {
  const { login } = useLogin();
  return (
    <TrackerDiv
      className={styles.login}
      data-gd-click="button_click"
      data-button-name="登陆注册"
      onClick={async () => {
        login({
          target: "blank",
        });
      }}
    >
      登录/注册
    </TrackerDiv>
  );
}

/**
 *
 * @param {{
 *  label: string,
 * href: string,
 * className: string,
 * tag: string,
 * tagShow: boolean,
 * } & React.HTMLAttributes<HTMLAnchorElement>}
 * @returns
 */
function NavLink({ label, href, className, tag, tagShow, ...rest }) {
  return (
    <Link href={href} passHref>
      <TrackerA
        className={classNames({
          [className]: !!className,
          [styles.redTag]: tagShow,
        })}
        data-gd-click="button_click"
        data-button-name={label}
        data-red-tag={tag}
        {...rest}
      >
        {label}
        {href.startsWith("https://") && (
          <HBIcon type="ic_open" className={styles.linkIcon} />
        )}
      </TrackerA>
    </Link>
  );
}
